import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useCamera from './useCamera';
import CameraDialog from './CameraDialog';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Steps } from 'primereact/steps';
import { MeterGroup } from 'primereact/metergroup';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { RadioButton } from 'primereact/radiobutton';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { AutoComplete } from 'primereact/autocomplete';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ColorPicker } from 'primereact/colorpicker';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { Image } from 'primereact/image';
import { Sidebar } from 'primereact/sidebar';
import { default as ListTable } from './listTable';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { ItemTemplate, EmptyTemplate } from './uploadFile';
import { utils } from '../../utils/utils';

import styles from './formComplete.module.scss';

function Form(props) {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const toast = useRef(null);
  const [step, setStep] = useState(1);
  const [renderKey, setRenderKey] = useState(); //eslint-disable-line
  const [pdfURL, setPdfURL] = useState();
  const [fileQuestions] = useState((props.fields || []).filter(q => q.type === 'file').map(q => q.name));
  const [submitDisabled, setIsSubmitDisabled] = useState(!!props.disableSubmit);
  const [submitted, setSubmitted] = useState(false);
  const refs = useRef([]);

  const methods = useForm({ defaultValues: generateDefault(props.fields) });

  const { control, formState: { errors }, formState, getValues, setValue, setError, clearErrors,
  } = methods;
  const controllerQuestions = (props.fields || []).reduce((listHTML, q) => {
    if (!q.size) q.size = q.col ? 12 : 6;
    listHTML[q.name] = ControllerHTML(q);
    return listHTML;
  }, {});


  function checkAndSetFieldValue(thing, question) {
    if (!thing.fieldState.isDirty) {
      const compare1 = thing.field.value ? thing.field.value.toString() : undefined;
      const compare2 = thing.field.value ? thing.field.value.toString() : undefined;
      if (compare1 !== compare2 && thing.field.value !== null) {
        setValue(thing.field.name, question.value || null); //must be null cant be undefined
      }
    }
  }

  function readFiles(files) {
    return Promise.all(files.map(file => utils.readFileAsDataURL(file)));
  }

  const onPreviousClick = () => {
    setStep(step - 1);
  };

  const cancelRequest = () => confirmDialog({
    message: translate('form.cancel.confirmation'),
    header: translate('form.cancel.header', { name: props.name }),
    icon: 'pi pi-info-circle',
    closeable: false,
    acceptClassName: 'p-button-danger',
    accept: function () { props.returnUrl ? navigate(props.returnUrl) : navigate(-1); },
    reject: function () { return null; },
    pt: {
      acceptButton: { root: { id: 'BTN_ACCEPT' } },
      rejectButton: { root: { id: 'BTN_RETURN' } }
    }
  });

  const onFormSubmit = async (data) => {
    let storageUploadFailed = false;
    if (step === props.stepsConfig.length) {
      if (props.saveAsDraftTitle) {
        localStorage.removeItem(props.saveAsDraftTitle);
      }
      setSubmitted(true);
      props.onFormSubmit(data, props.fields);
    }
    else {
      if (step === props.stepsConfig.length - 1) {
        setPdfURL(props.name.includes('purchase order') ? props.generatePdf(data) : null);
      }
      if (props.saveAsDraftTitle) {
        try {
          localStorage.setItem(props.saveAsDraftTitle, JSON.stringify(data));
        } catch (e) {
          storageUploadFailed = true;
        }
      }
      setStep(step + 1);
      isSubmitDisabled(1);
      if (storageUploadFailed) {
        setTimeout(() => showToastMsg('error', translate('form.error.title'), translate('form.error.localStorage')), 500);
      }
    }
  };

  function useCreateForm(questions, sidebar) {
    let form = [];
    let size = 0;
    let currentRow = [];
    let rowIndex = 0;

    function resetWindow() {
      size = 0;
      currentRow = [];
      rowIndex = 0;
    }

    function groupQuestions(questionGroup, acc) {
      function resetRow() {
        currentRow = [];
        size = 0;
        rowIndex++;
      }
      function addRowHTML() {
        if (acc) {
          accord = accord.concat(formRow(currentRow, controllerQuestions, rowIndex, acc));
        } else {
          form = form.concat(formRow(currentRow, controllerQuestions, rowIndex));
        }
      }
      let accord = [];
      questionGroup.forEach(question => {
        if ((size + question.size) > 12) {
          addRowHTML();
          resetRow();
        }
        currentRow.push(question);
        size = question.size + size;
        if (question.isLastOnLine) {
          addRowHTML();
          resetRow();
        }
      });
      if (currentRow.length) {
        addRowHTML();
      }
      resetWindow();
      if (acc) return accord;
    }

    const stepQuestions = questions.filter(q => {
      let isHiddenSet = !!q.hidden;
      let isHiddenFunction = false;
      if (isHiddenSet) isHiddenFunction = typeof q.hidden === 'function';
      const hidden = isHiddenSet
      ? isHiddenFunction ? q.hidden(getValues, questions) : q.hidden
      : false;

      return q.step === step && (sidebar || !hidden);
    });
    if (stepQuestions.length && stepQuestions[0].accordion) {
      const accordionSubSteps = stepQuestions.reduce((accordions, q) => {
        if (!accordions[q.accordion]) accordions[q.accordion] = [];
        accordions[q.accordion].push(q);
        return accordions;
      }, {});
      form = form.concat([<Accordion key={`form-accordion`} className='form-accordion md:col-10 col-12'>
        {Object.keys(accordionSubSteps).map(key => <AccordionTab key={key} header={key}>
          {groupQuestions(accordionSubSteps[key], true)}
        </AccordionTab>)}
      </Accordion>]);
    } if (stepQuestions.some(q => q.col)) {
      const noColQuestions = [];
      const columnQuestions = stepQuestions.sort((a, b) => a.col - b.col).reduce((columnBreak, question) => {
        if (!question.col) {
          noColQuestions.push(question);
          return columnBreak;
        }
        if (!columnBreak[question.col - 1]) columnBreak[question.col - 1] = [];
        columnBreak[question.col - 1].push(question);
        return columnBreak;
      }, []);
      form = form.concat([<div key={`form-row-${step}`} className='formgrid grid'>
        {columnQuestions.map((colQuestions, i) =>
          <div className={`col-12 md:col-${(12 / columnQuestions.length) - 1}`} key={`col-${i}`}>
            {groupQuestions(colQuestions, true)}
          </div>,
        )}
        {noColQuestions.length ? <div className='col-12'>{groupQuestions(noColQuestions, true)}</div> : null}
      </div>]);
    } else {
      groupQuestions(stepQuestions);
    }
    return form;
  }

  function formRow(questions, questionsHTML, key, acc) {
    return <div key={`row-${key}`} className={`${styles['form-row']} formgrid grid ${acc ? `col-12` : `md:col-10 col-12`}`}>
      {questions.map(question => <React.Fragment key={question.name}>
        {questionsHTML[question.name]}
      </React.Fragment>)}
    </div>;
  }

  function ControllerHTML(question) {
    return <div className={`${styles['form-row-question']} field md:col-${question.size} col-12 ${question.type === 'viewPDF' ? 'h-screen min-h-[75vh]' : ''}`}>
      
      <label htmlFor={question.name}>
        {!question.hideLabel ? utils.capitalize(translate(question.label) || translate(question.name)) : null}
      </label>
      {question.optional && !(question.tableInput || question.tableTrigger) ? <span className={styles['form-row-question-optional']}>{translate('form.optional')}</span> : null}
      {question.tooltip
        ? <div className={styles['form-row-question-tooltip']}>
          <Tooltip target='.custom-target-icon' />
          <i className='custom-target-icon pi pi-info'
            data-pr-tooltip={question.tooltip}
            data-pr-position='right'
            data-pr-at='right+5 top'
            data-pr-my='left center-2'>
          </i>
        </div>
        : null}
      <Controller
        name={question.name}
        control={control}
        rules={generateRules(question)}
        render={(thing) => {
          //this is to stop data carry over from one step to another
          checkAndSetFieldValue(thing, question);
          switch (question.type) {
            case 'inputText':
              return inputText(thing.field, question);
            case 'number':
              return inputNumber(thing.field, question);
            case 'textArea':
              return textArea(thing.field, question);
            case 'autoComplete':
              return autoComplete(thing.field, question);
            case 'colorPicker':
              return colourPicker(thing.field, question);
            case 'button':
              return button(thing, question);
            case 'dataTable':
              return dataTable(thing.field, question);
            case 'radio':
              return radioButton(thing.field, question);
            case 'calendar':
              return datePicker(thing.field, question);
            case 'dropdown':
              return dropdown(thing.field, question);
            case 'multiSelect':
              return multiSelect(thing.field, question);
            case 'checkbox':
              return checkbox(thing.field, question);
            case 'file':
              return fileupload(thing.field, question);
            case 'image':
              return image(thing.field, question);
            case 'viewPDF':
              return viewPDF();
            case 'title':
              return title(question);
            case 'custom':
              return question.customHTML?.({
                value: thing.field.value,
                onChange: thing.field.onChange,
                getValues: getValues,
              });
            default: return question;
          }
        }}
      />
      {getFieldErrorMessage(errors, question.name)}</div>;
  }

  function viewPDF() {
    return <iframe src={pdfURL} style={{ width: '100%', height: '95%' }} title={translate('form.pdfViewer')}></iframe>;
  }

  function title(question) {
    return <div className={`styles['form-title'] sub-title`}><h2>{translate(question.label)}</h2></div>;
  }

  function inputText(field, question) {
    return <InputText id={`TXT_${question.name.toUpperCase()}`} className='text-base focus:border w-full'
      value={question.value || ''} disabled={question.isDisabled}
      onChange={(e) => { field.onChange(e.target.value); question.value = e.target.value; }}
      placeholder={`${translate('form.pleaseEnter')} ${translate(question.label) || translate(question.name)}`}
    />;
  }
  
  function inputNumber(field, question) {
    return <InputNumber
      id={`NUM_${question.name.toUpperCase()}`}
      value={question.value || null}
      className='w-full'
      minFractionDigits={Math.min(Math.max(question.minDecimals ?? 0, 0), 20)}
      maxFractionDigits={Math.min(Math.max(question.isDecimal ? 2 : 0, question.minDecimals ?? 0), 20)}
      min={question.min ? question.min : 0}
      onChange={(e) => {
        question.value = e.value;
        if (question.onChange) {
          question.onChange(e.value, setValue);
        }
        field.onChange(e.value);
      }}
      placeholder={`${translate('form.pleaseEnter')} ${translate(question.label) || translate(question.name)}`}
      useGrouping={!question.isDecimal}
    />;

  }

  function textArea(field, question) {
    return <InputTextarea {...field} className='text-base focus:border w-full' id={`TXA_${question.name.toUpperCase()}`}
      value={question.value || ''} onChange={(e) => { field.onChange(e.target.value); question.value = e.target.value; }}
      placeholder={`${translate('form.pleaseEnter')} ${translate(question.label) || translate(question.name)}`} autoResize rows={5} />;
  }

  function autoComplete(field, question) {
    return <AutoComplete id={`ATC_${question.name.toUpperCase()}`} className='text-base focus:border w-full' inputClassName='w-full' {...field} value={question.value} suggestions={question.options}
      placeholder={`${translate('form.pleaseEnter')} ${translate(question.label) || translate(question.name)}`} disabled={question.isDisabled}
      completeMethod={question.search} onChange={(e) => { field.onChange(e.value); question.value = e.value; }}
      onSelect={(e) => { if (question.onChange)  question.onChange(e.value, setValue, getValues, props.fields, setRenderKey); }} 
      itemTemplate={question.itemTemplate} selectedItemTemplate={question.selectedItemTemplate} forceSelection={true} />
  }

  function radioButton(field, question) {
    if (!(question?.options || []).length) {
      question.options.push({ inputId: translate('form.yes'), value: true });
      question.options.push({ inputId: translate('form.no'), value: false });
    }
    return (<div className={`flex ${question.block ? styles['radio-question-block'] : 'justify-content-start '}`}>
      {question.options.map((option, index) => <div key={`${question.name}-${option.inputId}-${index}`} className={styles['radio-question']}>
        <RadioButton
          {...field} inputId={`RDO_${option.inputId.toUpperCase()}-${question.name.toUpperCase()}`} value={option.value} checked={question.value === option.value}
          label={`RDO_${option.inputId.toUpperCase()}-${question.name.toUpperCase()}`} disabled={question.isDisabled} onChange={(e) => {
            field.onChange(e.value);
            if (question.onChange) {
              question.onChange(e.value, setValue);
            }
            question.value = e.value; setRenderKey(Math.random());
          }}
        />
        <label htmlFor={`RDO_${option.inputId.toUpperCase()}-${question.name.toUpperCase()}`}>{translate(option.inputId)}</label>
      </div>)}
    </div>);
  }

  function dropdown(field, question) {
    return <Dropdown inputId={`DRP_${question.name.toUpperCase()}`} {...field} value={question.value} className='w-full' options={question.options} placeholder={`${translate('form.pleaseSelect')}${translate(question.label) || translate(question.name)}`}
      filter={question.filter} virtualScrollerOptions={{ itemSize: 50 }} optionLabel={question.optionLabel ? question.optionLabel : null}
      disabled={question.isDisabled}
      onChange={(e) => {
        field.onChange(e.value);
        question.value = e.value;
        if (question.onChange) {
          question.onChange(e.value, setValue, getValues);
        }
        if (question.triggerRender) { setRenderKey(Math.random()); }
      }}></Dropdown>;
  }

  function multiSelect(field, question) {
    return <MultiSelect inputId={`MSL_${question.name.toUpperCase()}`} {...field} value={question.value} onChange={(e) => {
      field.onChange(e.value); question.value = e.value;
      if (question.triggerRender) setRenderKey(Math.random());
    }}
      options={question.options} optionLabel={question.optionLabel ? question.optionLabel : null} selectalllabel={question.selectAllLabel ? translate('form.selectAll') : ''}
      placeholder={`${translate('form.pleaseSelect')}${translate(question.label) || translate(question.name)}`} className='w-full' showSelectAll={!!question.selectAllLabel} />;
  }

  function checkbox(field, question) {
    return <span className='flex justify-content-start'>
      <Checkbox id={`CHK_WRAPPER_${question.name.toUpperCase()}`} {...field} inputId={`CHK_${question.name.toUpperCase()}`} value={question.value} checked={question.value} className={styles['checkbox-question']}
        disabled={question.isDisabled} onChange={(e) => { field.onChange(e.checked); isSubmitDisabled(); question.value = e.checked; }}></Checkbox>
      { question.hideLabel && <label htmlFor={`CHK_${question.name.toUpperCase()}`}>{translate(question.label) || translate(question.name)}</label> }
    </span>;
  }

  function colourPicker(field, question) {
    return <ColorPicker id={`CLR_${question.name.toUpperCase()}`} {...field} value={question.value} onChange={(e) => { field.onChange(e.value); question.value = e.value; }} />;
  }

  const camera = useCamera();
  const cameraContextRef = useRef(null);

  // helper to push into FileUpload from camera
  const appendToFileUpload = (fuRef, file) => {
    if (fuRef?.getFiles && fuRef?.setFiles) {
      const current = fuRef.getFiles() ?? [];
      fuRef.setFiles([...current, file]);
    }
  };

  const handleCameraCapture = async () => {
    const nativeFile = await camera.capturePhoto();
    const ctx = cameraContextRef.current;
    if (nativeFile && ctx) {
      const translatedFile = await readFiles([nativeFile]);
      ctx.question.onChange?.(translatedFile, setValue);
      ctx.field.onChange([...(translatedFile)]);
      //fu : FileUpload dom instance
      //ctx.idx : index of the specific FileUpload (set when we click cam btn)
      const fu = refs.current[ctx.idx];
      appendToFileUpload(fu, nativeFile);
    }
    camera.stopCamera();
    cameraContextRef.current = null;
  };

  function fileupload(field, question) {
    return <div><FileUpload id={`FIL_${question.name.toUpperCase()}`} ref={element => (refs.current[fileQuestions.findIndex(name => question.name === name)] = element)}
      className='w-full' multiple={question.multiple} accept=".png,.jpg,.jpeg,.pdf" capture="camera"
      onSelect={async (e) => {
        const translatedFile = e.files && e.files.length ? await readFiles(e.files) : [];
        if (question.onChange) {
          question.onChange(translatedFile, setValue);
        }
        field.onChange([...(translatedFile)]);
      }}
      onClear={() => {
        question.onClear?.(setValue);
        field.onChange([]);
      }}
      itemTemplate={(file, options) => <ItemTemplate file={file} options={options} />}
      emptyTemplate={() => <EmptyTemplate />}
    />

      {!camera.isPhone && (
        <Button
          type="button"
          icon="pi pi-camera"
          label={translate('form.takePhoto')}
          className="p-button-outlined mt-2"
          onClick={() => {
            const idx = fileQuestions.findIndex(name => question.name === name);
            cameraContextRef.current = { field, question, idx };
            camera.startCamera();
          }}
        />
      )}
    </div>;
  }

  function datePicker(field, question) {
    function setValue(value) {
      field.onChange(value);
      question.value = value;
      if (question.onChange) {
        question.onChange(value, props.fields);
        if (question.triggerRender) setRenderKey(Math.random());
      }
    }

    return <Calendar inputId={`CAL_${question.name.toUpperCase()}`} {...field} value={question.value} minDate={question.options ? new Date(question.options.min) : new Date()} showTime={question.showDateTime} hourFormat='24'
      dateFormat="dd/mm/yy" disabled={question.isDisabled}
      onChange={(e) => setValue(e.value)} showIcon={question.showDateTime} timeOnly={question.timeOnly}
      placeholder={`${translate('form.pleaseSelect')} ${translate(question.label) || translate(question.name)}`} />;
  }

  function button(controller, question) {
    return <Button id={`BTN_${question.name.toUpperCase()}`} className='w-full' onClick={() => question.tableTrigger ? populateDataTable(controller, question.tableTrigger) : question.action()} {...controller.field}
      label={question.label || question.name} type='button' severity={question.primary ? 'secondary' : 'primary'} />; //yes it's backwards, its styled backwards
  }

  function dataTable(field, question) {
    return <ListTable question={question} removeRow={removeRow} isDisabled={question.isDisabled} className='w-full' />;
  }

  function image(field, question, hidePreview) {
    const unit8Array = new Uint8Array(question.data);
    const blob = new Blob([unit8Array], { type: 'image/png' });
    const url = window.URL.createObjectURL(blob);
    return <span className='flex justify-content-start'>
      <Image src={url} height={!hidePreview ? 60 : null} alt={question.name} preview={!hidePreview}></Image>
    </span>;
  }

  function getFieldErrorMessage(errors, fieldName) {
    return errors[fieldName]
      ? (
        <div className='text-red-500 mt-2'>{errors[fieldName].message}</div>
      )
      : null;
  }

  const showToastMsg = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail });
  };

  function generateDefault(fields) {
    return fields.reduce((defaultValues, field) => {
      defaultValues[field.name] = field.value;
      return defaultValues;
    }, {});
  }

  function generateRules(question) {
    let rules = {};
    if (!question.optional && question.type !== 'radio') {
      rules.required = `${utils.capitalize(translate(question.label) || translate(question.name))} ${translate('form.isRequired')}`;
    }
    if (question.pattern) {
      rules.pattern = question.pattern;
    }
    if (question.validate) {
      rules.validate = question.validate;
    }
    return rules;
  }

  function populateDataTable(controller, tableName) {
    const input = {};
    let error = false;
    const table = props.fields.find(field => field.name === tableName);
    const fields = props.fields.filter(field => field.tableInput === table.name);
    fields.forEach(question => {
      if ((question.value === null || question.value === undefined) && !question.allowTableNull) {
        error = true;
        setError(table.name, { type: 'custom', message: `${translate(question.name)} ${translate('form.isRequired')} ${translate('form.toAddDataToTable')}` });
      }
    });
    if (error) return;
    fields.forEach(question => {
      input[question.name] = question.value;
      question.value = question.default || null;
      question.isDisabled = question.isDisabled !== undefined ? question.isDisabled : null;
      setValue(question.name, question.value);
    });

    table.value.push(input);
    setValue(table.name, table.value);
    clearErrors(table.name);
  }

  function removeRow(index, tableName) {
    const table = props.fields.find(field => field.name === tableName);
    table.value.splice(index, 1);
    setValue(table.name, table.value);
  }

  function isSubmitDisabled(boost = 0) {
    let disabled = false;
    if (formState.isSubmitting) disabled = true;
    if (props.disableSubmit) disabled = props.disableSubmit(getValues, step + boost);
    setIsSubmitDisabled(disabled);
  }

  function styleColumns() {
    return props.stepsConfig[step - 1].hasHelperImage
      ? 'col-8'
      : 'md:col-offset-2 md:col-10 col-12';
  }

  function renderStepper() {
    return props.useMeter
      ? <div className='flex flex-column grid'>
        <div className='md:col-offset-2 md:col-8 col-12'>
          <MeterGroup aria-label="form-progress-meter"
            values={[
              { label: translate('form.progress.long', { step, total: props.stepsConfig.length, label: props.stepsConfig[step - 1].label }), value: Math.round((step / props.stepsConfig.length) * 100) },
            ]}
            labeltemplate={(item) => <span><strong>{`${translate('form.progress', { step, total: props.stepsConfig.length })}\t`}</strong>{props.stepsConfig[step - 1].label}</span>}
            readOnly
          />
        </div>
      </div>
      : <Steps
        model={props.stepsConfig.map((stepDef, i) => { stepDef.icon = step > (i + 1) ? 'pi pi-check' : ''; return stepDef; })}
        activeIndex={step - 1}
        onSelect={(e) => { if (e.index < step) setStep(e.index + 1); }}
        readOnly={false}
      />
  }

  function renderFooter() {
    return <div className={`${styles['form-buttons']} flex flex-row grid`}>
        <Button  id={`BTN_NEXT`} severity='secondary' type='submit' form="hook-form"
          loading={formState.isSubmitting} label={props.stepsConfig.length === step ? props.submitLabel : translate('form.next')}
          disabled={submitDisabled} className=' md:col-6 col-12 md:col-offset-2 mb-5' />
        {step > 1 && (
          <Button id={`BTN_PREV`}
            onClick={() => onPreviousClick()} className=' md:col-6 col-12 md:col-offset-2 mb-5'
            label={translate('form.previous')} type='button'  />
        )}
        <Button id={`BTN_REJECT`} tabIndex={-1} onClick={() => cancelRequest()}
        label={translate('form.reject')} type="button" className=' md:col-6 col-12 md:col-offset-2 mb-5' />
    </div>
  }

  return <div className={`${styles['form']} layer1 flex flex-column`}>
    <Toast ref={toast} />
    <ConfirmDialog />
    {props.stepsConfig && props.stepsConfig.length > 1
      ? renderStepper()
      : null}
    <div className={styles['form-section']}>
      <div className='flex flex-column'>
        <div className={styles['form-parent']}>
          <FormProvider {...methods}>
            <form id="hook-form" onSubmit={methods.handleSubmit(onFormSubmit)}>
              {!props.useMeter && <div className='flex flex-row'>
                <div className={styles['form-header']}>
                  {props.stepsConfig[step - 1].label}
                </div>
                <div className={styles['form-header-step-summary']}>{translate('form.progress', { step, total: props.stepsConfig.length })}</div>
              </div>}
              <div className='flex flex-row grid'>
                <div className={`${styles['form-wrapper']} ${styleColumns()}`}>
                  {useCreateForm(props.fields)}
                </div>
                {props.activeImage && props.stepsConfig[step - 1].hasHelperImage
                  ? <div className='col-4'>{image(null, props.activeImage, true)}</div>
                  : null
                }
              </div>
              {props.hideFooter && props.hideFooter(getValues, step) ? null : renderFooter()}
              <Sidebar id={`MDL_MOBILE`} visible={props.showSidebar && props.showSidebar(getValues, step) && !submitted} className={'light'} position="bottom">
                {useCreateForm(props.fields.filter(q => q.showInSidebar), true)}
                {renderFooter()}
              </Sidebar>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
    <CameraDialog
      cameraOpen={camera.cameraOpen}
      cameraError={camera.cameraError}
      videoRef={camera.videoRef}
      stopCamera={camera.stopCamera}
      onCapture={handleCameraCapture}
    />
  </div>;
}

Form.propTypes = {
  onFormSubmit: PropTypes.func,
  stepsConfig: PropTypes.array,
  fields: PropTypes.array,
  submitLabel: PropTypes.string,
  name: PropTypes.string,
  generatePdf: PropTypes.func,
  activeImage: PropTypes.object,
  saveAsDraftTitle: PropTypes.string,
  useMeter: PropTypes.bool,
  hideFooter: PropTypes.func,
  showSidebar: PropTypes.func,
  returnUrl: PropTypes.string,
  disableSubmit: PropTypes.func,
};

export default Form;
